import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { IInvoiceListsDateRange, InvoicingService } from '../../../core/services/invoicing.service';
import { MerchantsService } from '../../../core/services/merchants.service';
import { StoresService } from '../../../core/services/stores.service';
import { UserService } from '../../../core/services/user.service';
import { format } from 'date-fns';
import { DateRangeModalComponent } from '../../../shared/date-range-modal/date-range-modal.component';
import { SaleDetailsComponent } from '../sale-details/sale-details.component';
import { TranslatePipe } from '@ngx-translate/core';
import { BsDropdownDirective, BsDropdownMenuDirective, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';
import { DatePipe } from '@angular/common';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { SalesStatusComponent } from '../sales-status/sales-status.component';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { IconComponent } from '../../../shared-with-client/icon/icon.component';
import { EditCommissionModalComponent } from '../edit-commission-modal/edit-commission-modal.component';

@Component({
    selector: 'app-commission-list-page',
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        RouterLink,
        BsDropdownDirective,
        BsDropdownToggleDirective,
        BsDropdownMenuDirective,
        DatePipe,
        LoaderComponent,
        MerchantCurrencyPipe,
        SalesStatusComponent,
        ButtonComponent,
        IconComponent,
    ],
    templateUrl: './commission-list-page.component.html',
    styleUrl: './commission-list-page.component.scss',
})
export class CommissionListPageComponent {
    private merchantService = inject(MerchantsService);
    private storesService = inject(StoresService);
    private userService = inject(UserService);
    private modalService = inject(BsModalService);

    private invoicingService = inject(InvoicingService);
    private router = inject(Router);
    private fb = inject(FormBuilder);

    public currentMerchant = toSignal(this.merchantService.activeMerchant);
    public currentStore = toSignal(this.storesService.getActiveStore());
    public currentUser = toSignal(this.userService.currentUser);

    public loading = signal(true);
    public availableProviders = signal<any[]>([]);

    public commissionsData = signal<{ summary: any; commissions: any[] }>({ summary: [], commissions: [] });

    public searchForm!: FormGroup;

    public filterDateRanges = this.invoicingService.dateRanges;
    public selectedDateRange = signal(this.filterDateRanges[4]); // Este mês

    // Commissions page never shows cancelled sales; exclude from status filter options
    public saleStatuses = this.invoicingService.saleStatuses.filter((s) => s.value !== 'cancelled');
    
    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());

    ngOnInit(): void {
        this.setupForm();

        this.loadData();
    }

    setupForm(): void {
        const parsedDateRange = this.invoicingService.parseDateRange(this.selectedDateRange().value);
        this.searchForm = this.fb.group({
            provider: [null],
            status: [null],
            startDate: [parsedDateRange?.startDate],
            endDate: [parsedDateRange?.endDate],
        });
        this.storesService.getStoreProviders(this.currentStore().id).subscribe((providers) => {
            this.availableProviders.set(providers);
        });
        this.searchForm.valueChanges.subscribe(() => {
            // Handle search changes
            this.loadData();
        });
    }

    loadData(): void {
        this.loading.set(true);
        this.invoicingService
            .getCommissions({
                storeId: this.currentStore().id,
                providerId: this.searchForm.get('provider')?.value?.id ?? null,
                status: this.searchForm.get('status')?.value?.value ?? null,
                startDate: this.searchForm.get('startDate')?.value ?? null,
                endDate: this.searchForm.get('endDate')?.value ?? null,
            })
            .subscribe({
                next: (response) => {
                    this.commissionsData.set(response);
                    this.loading.set(false);
                },
                error: () => {
                    // In case of error, assume there's no overlap
                    this.loading.set(false);
                },
            });
    }

    setDateRange(range: any) {
        if (range.value === IInvoiceListsDateRange.CUSTOM) {
            this.openCustomRange(range);
        } else {
            this.selectedDateRange.set(range);
            const parsedRange = this.invoicingService.parseDateRange(range.value);
            this.searchForm.patchValue({
                startDate: parsedRange?.startDate,
                endDate: parsedRange?.endDate,
            });
        }
    }

    setProviderFilter(provider: any) {
        this.searchForm.patchValue({
            provider: provider,
        });
    }

    setSaleStatus(status: any) {
        this.searchForm.patchValue({
            status: status,
        });
    }

    openCustomRange(range: any) {
        const initialState = {
            data: {
                dateInit: null,
                dateEnd: null,
            },
        };

        this.modalService.show(DateRangeModalComponent, { initialState, class: 'modal-md' });

        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            if (reason && typeof reason === 'object') {
                const { dateInit, dateEnd } = reason as { dateInit: Date; dateEnd: Date };
                if (dateInit && dateEnd) {
                    this.searchForm.patchValue({
                        startDate: format(dateInit, 'yyyy-MM-dd'),
                        endDate: format(dateEnd, 'yyyy-MM-dd'),
                    });
                }
                this.selectedDateRange.set(range);
            }
            modalCallback.unsubscribe();
        });
    }

    editCommission(line: any): void {
        this.modalService.show(EditCommissionModalComponent, { initialState: { commission: line }, class: 'modal-md' });
        this.modalService.onHide.subscribe((reason: string) => {
            if (reason) {
                this.loadData();
            }
        });
    }

    seeDetails(saleId: any): void {
        this.invoicingService.getSaleDetails(saleId, this.currentStore().id).subscribe((sale) => {
            this.modalService.show(SaleDetailsComponent, { initialState: { sale }, class: 'modal-lg' });
            this.modalService.onHide.subscribe((reason: string) => {
                if (reason) {
                    this.loadData();
                }
            });
        });
    }
}
